import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { httpBuildQuery } from 'helpers/utils';

import { useDebounce } from 'hooks/useDebounce';
import { Stock } from 'store/stock/actions';
import { Instrument } from 'store/transferAct/actions';
import { stockListSelector } from 'store/stock/selectors';
import { balanceListSelector, subtypesListSelector, categoriesListSelector } from 'store/transferAct/selectors';

import './style.scss';
import { balanceApi } from 'helpers/api/balance';
import { ModalInstrumentsBase } from './ModalInstrumentsBase';
import { ContextMenu, MenuItem } from 'react-contextmenu';

const contextMenuID = 'context-menu';

const ModalInstruments = ({ selected, defaultStock, disableSelectable, contextMenuID, onAdd, onClose }) => {
  const dispatch = useDispatch();
  const stockList = useSelector(stockListSelector);
  const categoriesList = useSelector(categoriesListSelector);
  const instrumentsList = useSelector(balanceListSelector);
  const subTypeList = useSelector(subtypesListSelector);

  const [category, setCategory] = useState(null);
  const [search, setSearch] = useState('');
  const [searchCategory, setSearchCategory] = useState('');
  const [articul, setArticul] = useState('');
  const [subtype, setSubType] = useState('');
  const [stock, setStock] = useState(defaultStock || { stockID1C: '', stockName: '' });

  const debouncedSearch = useDebounce(search, 500, true);
  const debouncedArticul = useDebounce(articul, 500, true);

  useEffect(() => {
    if (stockList === null) {
      dispatch(Stock.getStockList());
    }

    if (subTypeList.length === 0) {
      dispatch(Instrument.getSubTypes());
    }

    dispatch(Instrument.saveBalanceList([]));
    dispatch(Instrument.getCategories());
  }, []);

  useEffect(() => {
    filterInstruments();
  }, [category, subtype, stock]);

  useEffect(() => {
    if (
      debouncedSearch.length >= 2 ||
      debouncedArticul.length >= 2 ||
      (debouncedArticul.length === 0 && debouncedSearch.length === 0)
    ) {
      filterInstruments();
    }
  }, [debouncedSearch, debouncedArticul]);

  const filterInstruments = () => {
    if (!category && debouncedArticul.length < 2 && debouncedSearch.length < 2) {
      dispatch(Instrument.saveBalanceList([]));
      return;
    }

    const value = {};

    if (category) {
      value.category = category;
    }

    if (debouncedSearch.length >= 2) {
      value.title = debouncedSearch;
    }

    if (debouncedArticul.length >= 2) {
      value.articul = debouncedArticul;
    }

    if (subtype.length > 0) {
      value.subtype = subtype;
    }

    if (stock?.stockID1C) {
      value.stockID = stock.stockID1C;
    }

    dispatch(Instrument.getBalanceList(value));
  };

  const toggleCategory = (value) => {
    if (category === value) {
      setCategory(null);
      return;
    }

    setCategory(value);
  };

  const handleSearch = (value) => {
    setSearch(value);
  };

  const handleSubType = (value) => {
    setSubType(value);
  };

  const handleArticul = (value) => {
    setArticul(value);
  };

  const handleStock = (value) => {
    setStock(value);
  };

  const handleSearchCategory = (value) => {
    setSearchCategory(value);
  };

  const getItem = async (item) => {
    let params = {};
    if (stock?.stockID1C) {
      params.stockID = stock.stockID1C;
    }
    const { data: data } = await balanceApi.getCurrentBalance(item.id, params);
    return { ...item, ...data };
  };

  const handleContextMenu = async (_, { stock, id, stockID }) => {
    const { data: item } = await balanceApi.getCurrentBalance(id);
    const params = { item: item.itemID };
    const stockIDQueryParam = stockID || stock?.stockID1C;
    if (stockIDQueryParam) {
      params.stock = stockIDQueryParam;
    }
    window.open(`${window.location.origin}/${httpBuildQuery(params)}`, '_blank');
  };

  const handleSelectItem = async (item) => {
    if (!disableSelectable) {
      const data = await getItem(item);
      return onAdd(data);
    }
  };
  const handleReset = () => {
    setSearch('');
    handleStock({ stockID1C: '', stockName: '' });
    handleSubType('');
    handleArticul('');
  };

  const canReset = useCallback(() => {
    return debouncedSearch.length >= 2 || debouncedArticul.length >= 2 || subtype || stock?.stockID1C;
  }, [debouncedSearch, debouncedArticul, subtype, stock]);

  return (
    <>
      <ModalInstrumentsBase
        articul={articul}
        handleArticul={handleArticul}
        selected={selected}
        searchCategory={searchCategory}
        category={category}
        toggleCategory={toggleCategory}
        handleSearchCategory={handleSearchCategory}
        categoriesList={categoriesList}
        handleStock={handleStock}
        subtype={subtype}
        subTypeList={subTypeList}
        handleSubType={handleSubType}
        handleSearch={handleSearch}
        contextMenuID={contextMenuID}
        instrumentsList={instrumentsList}
        stock={stock}
        handleSelectItem={handleSelectItem}
        stockList={stockList}
        onClose={onClose}
        onAdd={onAdd}
        canReset={canReset}
        handleReset={handleReset}
      />
      <ContextMenu id={contextMenuID} className="modal-instrument-context-menu">
        <MenuItem onClick={handleContextMenu}>Текущие аренды и резервы</MenuItem>
      </ContextMenu>
    </>
  );
};

ModalInstruments.propTypes = {
  selected: PropTypes.array,
  disableSelectable: PropTypes.bool,
  contextMenuID: PropTypes.string,
  defaultStock: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onAdd: PropTypes.func,
};

ModalInstruments.defaultProps = {
  selected: [],
  disableSelectable: false,
  contextMenuID: 'defaultId',
  defaultStock: { stockID1C: '', stockName: '' },
  onAdd: () => {},
};

export { ModalInstruments };
